import type {MutationTree} from 'vuex'
import type {MymachineState} from './initial-state'
import _merge from 'lodash.merge'
import router from '@/router'
import constants from '@/constants/others'

const mutations: MutationTree<MymachineState> = {
  restoreState(state) {
    let stateRestored: Record<string, any> = {}

    try {
      stateRestored = localStorage.getItem('mymachine_state')
        ? JSON.parse(localStorage.getItem('mymachine_state')!)
        : {}
    } catch (e) {
      console.error('Error parsing JSON string', e)
    }

    _merge(state, stateRestored)
    state.stateRestored = true
  },
  saveFeatures(state, payload: Record<string, boolean>) {
    state.features = payload
  },
  addSerialNumber(state, serial: any) {
    const userKey = (this as any).getters['mymachine/getCurrentUserKey']
    const currentRegion = (this as any).getters['mymachine/getRegion']

    if (!userKey || !currentRegion) return

    if (!(this as any).getters['mymachine/getSerials']) {
      const serialsCopy = _merge({}, state.serials)

      if (!serialsCopy[userKey]) serialsCopy[userKey] = {[currentRegion]: []}
      else if (!serialsCopy?.[userKey]?.[currentRegion]) serialsCopy[userKey][currentRegion] = []

      state.serials = serialsCopy
    }

    /* fix for old structure */
    if (state.serials?.[userKey]?.length && 0 in state.serials?.[userKey]) {
      state.serials[userKey] = {NA: state.serials[userKey]}
    }

    const currentSerials = (this as any).getters['mymachine/getSerials']
    const serialExistsInCurrentState = currentSerials.find(
      (stateSerial: any) => stateSerial.serial_number === serial.serial_number
    )

    if (!serialExistsInCurrentState) {
      serial.active = true
      state.serials[userKey][currentRegion].unshift(serial)
    }

    state.serials[userKey][currentRegion] = state.serials[userKey][currentRegion].slice(0, 10)
    ;(this as any).commit('mymachine/activateSerialNumber', serial)
  },
  updateSerialNumber(state, serial: any) {
    const userKey = (this as any).getters['mymachine/getCurrentUserKey']
    const currentRegion = (this as any).getters['mymachine/getRegion']

    if (!userKey || !currentRegion) return

    const serialsCopy = _merge({}, state.serials)
    const index = serialsCopy[userKey][currentRegion].findIndex(
      (x: any) => x.serial_number === serial.serial_number
    )
    if (index >= 0) {
      serial.active = serialsCopy[userKey][currentRegion][index].active
      serialsCopy[userKey][currentRegion][index] = serial
    }

    state.serials = serialsCopy
  },
  removeSerialNumber(state, serial: any) {
    const userKey = (this as any).getters['mymachine/getCurrentUserKey']
    const currentRegion = (this as any).getters['mymachine/getRegion']

    if (!userKey || !currentRegion) return
    if (!state.serials?.[userKey]?.[currentRegion]) return

    const serialNumber = typeof serial === 'object' ? serial.serial_number : serial
    state.serials[userKey][currentRegion] = state.serials[userKey][currentRegion].filter(
      (stateSerial: any) => stateSerial.serial_number !== serialNumber
    )

    const isActiveSerialInState = state.serials[userKey][currentRegion].find(
      (s: any) => s.active === true
    )

    const serialStateLength = !!state.serials[userKey][currentRegion].length
    if (!isActiveSerialInState && serialStateLength) {
      const firstSerialInState = state.serials[userKey][currentRegion][state.serials[userKey][currentRegion].length - 1]
      ;(this as any).commit('mymachine/activateSerialNumber', firstSerialInState)
    }

    state.serials = Object.assign({}, state.serials)
  },
  activateSerialNumber(state, serial: any) {
    const userKey = (this as any).getters['mymachine/getCurrentUserKey']
    const currentRegion = (this as any).getters['mymachine/getRegion']

    if (!userKey || !currentRegion) return
    if (!state.serials?.[userKey]?.[currentRegion]) return

    const serialNumber = typeof serial === 'object' ? serial.serial_number : serial
    state.serials[userKey][currentRegion] = state.serials[userKey][currentRegion].map(
      (stateSerial: any) => {
        stateSerial.active = stateSerial.serial_number === serialNumber
        return stateSerial
      }
    )

    state.serials = Object.assign({}, state.serials)
  },
  saveTroubleshootingKeyword(state, k: string) {
    state.ui.troubleshooting.keyword = k
  },
  saveTroubleshootingSearchType(state, st: string) {
    state.ui.troubleshooting.searchType = st
  },
  saveUser(state, u: Record<string, any>) {
    state.user = u
  },
  saveIdentity(state, i: Record<string, any>) {
    state.identity = i
  },
  saveRegion(state, payload: string | { region: string; redirectToDashboard: boolean }) {
    const newRegion = typeof payload === 'string' ? payload : payload.region
    const redirectToDashboard = typeof payload === 'string' ? false : payload.redirectToDashboard
    const _key = (this as any).getters['mymachine/getCurrentUserKey']
    state.region[_key] = newRegion
    state.region = Object.assign({}, state.region)

    const newQuery: Record<string, string> = {}

    const currentSerial = (this as any).getters['mymachine/getCurrentSerial']
    if (currentSerial) {
      newQuery.serial = currentSerial
    }

    newQuery[constants.REGION_QUERY_NAME] = newRegion

    if (redirectToDashboard) {
      router.replace({name: 'Dashboard', query: newQuery})
    }
  },
  saveLanguage(state, l: string) {
    const _key = (this as any).getters['mymachine/getCurrentUserKey']
    const _region = (this as any).getters['mymachine/getRegion']
    if (!_key || !_region) return
    state.language[_key] = state.language[_key] || {}
    state.language[_key][_region] = l
    state.language = Object.assign({}, state.language)
  },
  saveMachine(state, payload: any) {
    state.ui.currentMachine = {
      ...state.ui.currentMachine,
      [payload.sn]: Object.assign(state.ui.currentMachine[payload.sn] || {}, payload)
    }
  },
  saveMachineAuth(state, payload: { sn: string; val: boolean }) {
    state.ui.authorisation = {
      ...state.ui.authorisation,
      [payload.sn]: Object.assign(state.ui.authorisation[payload.sn] || {}, {machineAuthorized: payload.val})
    }
  },
  saveRegionConcept(state, payload: { region: string; data: Record<string, any> }) {
    state.regionConcepts = {...state.regionConcepts, [payload.region]: payload.data}
  },
  faultcodesKeywordSave(state, k: string) {
    state.faultcodes.keyword = k
  },
  saveOrganization(state, payload: { sn: string; org: any }) {
    state.organizations = {...state.organizations, [payload.sn]: payload.org}
  },
  saveFilteredOrganization(state, payload: { name: string; org: any }) {
    state.filteredOrganization = {...state.filteredOrganization, [payload.name]: payload.org}
  },
  saveSNErrorStatus(state, wasChanged: boolean) {
    state.ui.wrongSN = wasChanged
  },
  saveMachinePassword(state, machinePassword: string | null) {
    state.machinePassword = machinePassword
  },
  saveShowConcepts(state, payload: boolean) {
    state.showConceptNames = payload
  },
  saveFilters(state, payload: any) {
    const userKey = (this as any).getters['mymachine/getCurrentUserKey']
    if (!state.filters) state.filters = {}
    state.filters = {...state.filters, [userKey]: payload}
  },
  resetFilters(state) {
    const userKey = (this as any).getters['mymachine/getCurrentUserKey']
    if (state.filters[userKey]) {
      const {[userKey]: _, ...rest} = state.filters
      state.filters = rest
    }
  }
}

export default mutations
